import { useState, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCurrentUser, useMyProfile, loadSavedAccounts, getSupabase, copyToClipboard } from '@dogdisk/shared'
import { useQuery } from '@tanstack/react-query'
import { MobileScreen } from '../components/mobile-screen'
import { TabBar } from '../components/tab-bar'
import { NodeTestModal } from '../components/modals/node-test-modal'
import { AccountWarningBox } from '../components/account-warning-box'
import { useResourceNodes } from '../_lib/use-resource-nodes'
import { useNodePref } from '../_lib/use-node-pref'
import { resourceUrl } from '../_lib/resource-node-pref'
import { useCoinBalance } from '../_lib/wallet-api'
import { useMyVip, PLAN_TYPE_ORDER, TIER_NAMES, tierToIndex } from '../_lib/vip-api'
import type { PlanType } from '../_lib/vip-api'

// 切换节点(Isk7n):节点来自 v_resource_node_public(真实资源 CDN 域名),
// 「测速」按钮触发真人测速 + fire-and-forget 回传(useResourceNodes)。
// client-side 选择,不持久化(D28 匿名不落库)。

/**
 * 我的 — has two visual states (frame EKOSx 未登录 / QQKOK 已登录).
 * Per "默认浏览数据无需登录": this page is reachable for guests too,
 * but the body switches to a login CTA + lightweight links.
 */
export function ProfilePage() {
  const navigate = useNavigate()
  const { user, status, signOut } = useCurrentUser()

  return (
    <MobileScreen>
      <header className="flex items-center justify-between px-6 pt-[44px] pb-3">
        <h1 className="text-[22px] font-bold text-ink">我的网盘</h1>
        <button
          type="button"
          onClick={() => navigate('/messages')}
          className="relative flex h-9 w-9 items-center justify-center rounded-full bg-surface-100"
          aria-label="通知"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink"><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 01-3.4 0" /></svg>
          <span className="absolute right-1.5 top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger-500 px-1 text-[9px] font-semibold text-white">
            3
          </span>
        </button>
      </header>

      {status === 'authenticated' && user ? (
        <AuthedBody
          user={user}
          onSignOut={() => void signOut().then(() => navigate('/auth/login', { replace: true }))}
        />
      ) : (
        <GuestBody />
      )}


      <TabBar />
    </MobileScreen>
  )
}

function GuestBody() {
  const navigate = useNavigate()
  const [nodeOpen, setNodeOpen] = useState(false)
  const { nodes: cdnNodes, pinging, pingAll } = useResourceNodes()
  const { currentNodeId, applyNode } = useNodePref(cdnNodes)
  // frame: EKOSx (guest 未登录视图)
  return (
    <main data-testid="EKOSx" className="flex flex-1 flex-col px-6 pt-4 pb-safe-tab">
      {/* 欢迎区 — 垂直居中列 */}
      <div className="flex flex-col items-center py-8 gap-4">
        <span className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F6F7F8]">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="text-[#9CA3AF]"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
        </span>
        <p className="text-[22px] font-bold text-ink">欢迎使用我的网盘</p>
        <p className="text-[17px] text-[#9CA3AF]">登录后享受更多功能</p>
        {/* 按钮组 */}
        <div className="flex w-full flex-col gap-3 pt-4">
          <button
            type="button"
            data-testid="EKOSx-login-btn"
            onClick={() => navigate('/auth/login/saved')}
            className="h-12 w-full rounded-xl bg-[#19caad] text-[18px] font-semibold text-white active:opacity-80"
          >
            登录
          </button>
          <button
            type="button"
            data-testid="EKOSx-register-btn"
            onClick={() => navigate('/auth/register')}
            className="h-12 w-full rounded-xl border border-[#E5E7EB] bg-[#bee7e9] text-[18px] font-semibold text-[#1A1A1A] active:opacity-80"
          >
            快速注册
          </button>
        </div>
      </div>

      {/* MenuCard HsHtQ — 仅切换节点 */}
      <ul className="flex flex-col overflow-hidden rounded-2xl bg-[#F6F7F8]">
        <li>
          <button
            type="button"
            onClick={() => setNodeOpen(true)}
            className="flex h-14 w-full items-center gap-3 px-4 text-left active:bg-surface-200"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
            <span className="flex-1 text-[14px] text-ink">切换节点</span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="text-border-strong"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        </li>
      </ul>

      <NodeTestModal
        open={nodeOpen}
        onClose={() => setNodeOpen(false)}
        nodes={cdnNodes}
        currentNodeId={currentNodeId}
        onApply={applyNode}
        onPingAll={pingAll}
        pinging={pinging}
      />
    </main>
  )
}

/** 公开主页数据(仅取 public_pack_count) */
function usePublicPackCount(userId: string) {
  return useQuery({
    queryKey: ['public-profile-count', userId] as const,
    enabled: !!userId,
    queryFn: async (): Promise<number | null> => {
      const sb = getSupabase()
      const { data } = await sb
        .from('v_profile_public')
        .select('public_pack_count')
        .eq('user_id', userId)
        .maybeSingle()
      // 关闭公开主页时 view 无行,不显示包数 hint(避免误导性 0)
      if (data === null) return null
      return (data as { public_pack_count: number }).public_pack_count
    },
    staleTime: 60_000,
  })
}

/** VIP chip 颜色配置 */
const VIP_CHIP_STYLE: Record<PlanType, { bg: string; border: string; text: string }> = {
  user: { bg: 'bg-[#FFF6D6]', border: 'border-[#F0D98C]', text: 'text-[#8B6914]' },
  forum_creator: { bg: 'bg-[#E8F1FF]', border: 'border-[#BBD4F5]', text: 'text-[#2563A8]' },
  dating_creator: { bg: 'bg-[#FFEAF1]', border: 'border-[#F5BBD0]', text: 'text-[#C2306B]' },
}

const VIP_CHIP_LABEL: Record<PlanType, string> = {
  user: '开通VIP',
  forum_creator: '论坛创作者',
  dating_creator: '约爱创作者',
}

/** VIP chip 图标 */
function VipChipIcon({ planType }: { planType: PlanType }) {
  const common = {
    width: 15,
    height: 15,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
  if (planType === 'user') {
    // crown
    return <svg {...common}><path d="M2 20h20M5 20V9l7-5 7 5v11" /><path d="M12 4v1" /><path d="M3 9l9 3 9-3" /></svg>
  }
  if (planType === 'forum_creator') {
    // message-square
    return <svg {...common}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
  }
  // dating_creator: heart
  return <svg {...common}><path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z" /></svg>
}

function AuthedBody({
  user,
  onSignOut,
}: {
  user: NonNullable<ReturnType<typeof useCurrentUser>['user']>
  onSignOut: () => void
}) {
  const navigate = useNavigate()
  const { data: wallet } = useCoinBalance(user.user_id)
  const { data: profile } = useMyProfile()
  const { data: myVip } = useMyVip(true)
  const { data: publicPackCount } = usePublicPackCount(user.user_id)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [nodeOpen, setNodeOpen] = useState(false)
  const [uidCopied, setUidCopied] = useState(false)
  const { nodes: cdnNodes, pinging, pingAll } = useResourceNodes()
  const { currentNodeId, applyNode } = useNodePref(cdnNodes)

  // passwordSavedLocally: 本机 saved-accounts 中该账号有 password 字段
  const passwordSavedLocally = useMemo(
    () => loadSavedAccounts().some((a) => a.user_id === user.user_id && !!a.password),
    [user.user_id],
  )

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleCopyUid = () => {
    void copyToClipboard(user.user_id).then((ok) => {
      if (!ok) return // 复制失败(权限拒绝/不可用):按钮不切换到已复制态
      setUidCopied(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => setUidCopied(false), 1500)
    })
  }
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current) }, [])

  return (
    <>
      {/* frame: QQKOK (已登录视图) */}
      <main data-testid="QQKOK" className="flex flex-1 flex-col gap-4 px-6 pt-2 pb-safe-tab">

        {/* ── UserSection ── */}
        <div className="flex flex-col gap-3 pt-2">
          {/* topRow */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="QQKOK-avatar"
              onClick={() => navigate(`/u/${user.user_id}`)}
              className="relative h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-full bg-border-subtle"
              aria-label="查看我的公开主页"
            >
              {user.avatar_url ? (
                <img src={resourceUrl(user.avatar_url)} alt={user.username} className="h-full w-full object-cover" />
              ) : (
                <span className="flex h-full w-full items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-ink-mute">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4 4-7 8-7s8 3 8 7" />
                  </svg>
                </span>
              )}
            </button>

            {/* 昵称 + 用户名 + 邮箱(对齐 .pen nwZDO) */}
            <div className="flex flex-1 flex-col gap-1">
              <p className="font-['Bricolage_Grotesque',sans-serif] text-[20px] font-bold text-ink leading-tight">
                {profile?.nickname || user.username}
              </p>
              <p className="text-[14px] text-[#9CA3AF] leading-snug">
                用户名: {user.username}
              </p>
              <p className="text-[14px] text-[#9CA3AF] leading-snug">
                邮箱: {user.email || profile?.email_real || '未绑定'}
              </p>
            </div>

            {/* 设置按钮 */}
            <button
              type="button"
              data-testid="QQKOK-settings"
              onClick={() => navigate('/profile/settings')}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F6F7F8] active:bg-surface-200"
              aria-label="账户设置"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-ink-mute">
                <circle cx="12" cy="12" r="3" />
                <path d="M19 12a7 7 0 00-.2-1.6l2-1.5-2-3.4-2.3 1a7 7 0 00-2.7-1.6L13 2.5h-4l-.8 2.4a7 7 0 00-2.7 1.6l-2.3-1-2 3.4 2 1.5A7 7 0 003 12c0 .5.07 1.1.2 1.6l-2 1.5 2 3.4 2.3-1a7 7 0 002.7 1.6L9 21.5h4l.8-2.4a7 7 0 002.7-1.6l2.3 1 2-3.4-2-1.5c.13-.5.2-1.1.2-1.6z" />
              </svg>
            </button>
          </div>

          {/* UID 行 */}
          <div className="flex items-center justify-between rounded-[10px] bg-[#F6F7F8] px-3 py-2.5">
            <span className="text-[13px] font-semibold text-[#9CA3AF]">UID</span>
            <span className="text-[13px] text-[#6B7280] tabular-nums">
              {user.user_id.slice(0, 6)}***{user.user_id.slice(-6)}
            </span>
            <button
              type="button"
              data-testid="QQKOK-uid-copy"
              onClick={handleCopyUid}
              className="flex h-7 w-7 items-center justify-center rounded text-[#9CA3AF] active:text-ink-mute"
              aria-label="复制 UID"
            >
              {uidCopied ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#19caad" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" />
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ── VIP 三胶囊行 ── */}
        <div data-testid="QQKOK-vip-chips" className="flex flex-wrap gap-2.5">
          {PLAN_TYPE_ORDER.map((planType) => {
            const seg = myVip?.active_segments.find((s) => s.plan_type === planType)
            const hasActive = !!seg
            const style = hasActive
              ? VIP_CHIP_STYLE[planType]
              : { bg: 'bg-[#F6F7F8]', border: 'border-[#E5E7EB]', text: 'text-[#9CA3AF]' }
            const label = hasActive && planType === 'user'
              ? TIER_NAMES[tierToIndex(seg!.tier)]
              : VIP_CHIP_LABEL[planType]

            return (
              <button
                key={planType}
                type="button"
                data-testid={`QQKOK-vip-chip-${planType}`}
                onClick={() => navigate('/vip/buy')}
                className={`flex items-center gap-1.5 rounded-[14px] border px-[11px] py-[7px] text-[13px] font-semibold ${style.bg} ${style.border} ${style.text}`}
              >
                <VipChipIcon planType={planType} />
                {label}
              </button>
            )
          })}
        </div>

        {/* ── AccountWarningBox ── */}
        {profile && (
          <AccountWarningBox
            usernameConfirmedAt={profile.username_confirmed_at}
            emailVerifiedAt={profile.email_verified_at}
            phoneReal={profile.phone_real}
            passwordChangedAt={profile.password_changed_at}
            passwordSavedLocally={passwordSavedLocally}
          />
        )}

        {/* ── 组1 我的资产 ── */}
        <ul className="flex flex-col overflow-hidden rounded-2xl bg-surface-100">
          <ProfileRow
            icon="node"
            label="切换节点"
            onClick={() => setNodeOpen(true)}
            testId="QQKOK-menu-node"
          />
          <ProfileRow
            icon="wallet"
            label="钱包余额"
            value={`${wallet?.balance ?? '—'} 金币`}
            onClick={() => navigate('/wallet')}
            testId="QQKOK-menu-wallet"
          />
          <ProfileRow
            icon="orders"
            label="购买记录"
            onClick={() => navigate('/orders')}
            testId="QQKOK-menu-orders"
          />
          <ProfileRow
            icon="vip"
            label="VIP详情"
            onClick={() => navigate('/vip')}
            testId="QQKOK-menu-vip"
          />
        </ul>

        {/* ── 组2 ── */}
        <ul className="flex flex-col overflow-hidden rounded-2xl bg-surface-100">
          <ProfileRow
            icon="user"
            label="个人资料"
            onClick={() => navigate('/profile/edit')}
            testId="QQKOK-menu-profile-edit"
          />
          <ProfileRow
            icon="public"
            label="我的主页"
            {...(publicPackCount != null ? { value: `${publicPackCount} 公开包` } : {})}
            onClick={() => navigate(`/u/${user.user_id}`)}
            testId="QQKOK-menu-homepage"
          />
          <ProfileRow
            icon="article"
            label="我的文章"
            onClick={() => navigate('/me/articles')}
            testId="QQKOK-menu-my-articles"
          />
          <ProfileRow
            icon="trash"
            label="文件回收站"
            onClick={() => navigate('/trash')}
            testId="QQKOK-menu-file-trash"
          />
          <ProfileRow
            icon="trash"
            label="资源包回收站"
            onClick={() => navigate('/pack-trash')}
            testId="QQKOK-menu-pack-trash"
          />
          <ProfileRow
            icon="ticket"
            label="客服工单"
            onClick={() => navigate('/tickets')}
            testId="QQKOK-menu-tickets"
          />
        </ul>

        <button
          type="button"
          data-testid="QQKOK-logout"
          onClick={() => setConfirmOpen(true)}
          className="mt-2 h-12 rounded-lg border border-border-subtle bg-white text-[14px] font-semibold text-danger-500 active:bg-danger-50"
        >
          退出登录
        </button>
      </main>
      <LogoutConfirmModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          onSignOut()
        }}
      />
      <NodeTestModal
        open={nodeOpen}
        onClose={() => setNodeOpen(false)}
        nodes={cdnNodes}
        currentNodeId={currentNodeId}
        onApply={applyNode}
        onPingAll={pingAll}
        pinging={pinging}
      />
    </>
  )
}

// frame: QOjZM @ ui/dogdisk-user.pen — 退出登录确认 modal
function LogoutConfirmModal({
  open,
  onCancel,
  onConfirm,
}: {
  open: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  if (!open) return null
  return (
    <div
      data-frame-id="QOjZM"
      data-testid="QOjZM"
      role="dialog"
      aria-modal="true"
      aria-label="确认退出登录"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[320px] rounded-2xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-center text-[16px] font-semibold text-[#1A1A1A]">确认退出登录</h2>
        <p className="mt-2 text-center text-[13px] text-[#9CA3AF]">
          退出后需重新登录才能访问个人数据
        </p>
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            data-testid="QOjZM-confirm"
            onClick={onConfirm}
            className="h-11 w-full rounded-lg bg-danger-500 text-[14px] font-semibold text-white active:bg-danger-600"
          >
            确认退出
          </button>
          <button
            type="button"
            data-testid="QOjZM-cancel"
            onClick={onCancel}
            className="h-11 w-full rounded-lg bg-[#F6F7F8] text-[14px] font-semibold text-[#1A1A1A]"
          >
            取消
          </button>
        </div>
      </div>
    </div>
  )
}

function ProfileRow({
  icon,
  label,
  value,
  onClick,
  testId,
}: {
  icon: string
  label: string
  value?: string
  onClick: () => void
  testId?: string
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="flex h-14 w-full items-center gap-3 border-b border-white/0 px-4 text-left active:bg-surface-200"
      >
        <span className="text-ink-mute"><RowIcon name={icon} /></span>
        <span className="flex-1 text-[14px] text-ink">{label}</span>
        {value && <span className="text-[13px] tabular-nums text-ink-mute">{value}</span>}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="text-border-strong"><path d="M9 6l6 6-6 6" /></svg>
      </button>
    </li>
  )
}


export function RowIcon({ name }: { name: string }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }
  switch (name) {
    case 'article':
      return <svg {...common}><path d="M5 3h11l3 3v15H5z" /><path d="M9 9h6M9 13h6M9 17h4" /></svg>
    case 'wallet':
      return <svg {...common}><path d="M3 7h18v12H3z" /><path d="M16 13h2" /></svg>
    case 'orders':
      return <svg {...common}><path d="M5 3h11l3 3v15H5z" /><path d="M9 12h6M9 16h6" /></svg>
    case 'vip':
      return <svg {...common}><path d="M3 8l4 8 5-12 5 12 4-8" /></svg>
    case 'invite':
      return <svg {...common}><path d="M20 12H4M14 6l6 6-6 6" /></svg>
    case 'trash':
      return <svg {...common}><path d="M3 6h18M8 6l1-3h6l1 3M6 6l1 14h10l1-14" /></svg>
    case 'ticket':
      return <svg {...common}><path d="M3 8a2 2 0 012-2h14a2 2 0 012 2v3a2 2 0 100 2v3a2 2 0 01-2 2H5a2 2 0 01-2-2v-3a2 2 0 100-2V8z" /></svg>
    case 'mfa':
      return <svg {...common}><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" /></svg>
    case 'bell':
      return <svg {...common}><path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 01-3.4 0" /></svg>
    case 'settings':
      return <svg {...common}><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 00-.2-1.6l2-1.5-2-3.4-2.3 1a7 7 0 00-2.7-1.6L13 2.5h-4l-.8 2.4a7 7 0 00-2.7 1.6l-2.3-1-2 3.4 2 1.5A7 7 0 003 12c0 .5.07 1.1.2 1.6l-2 1.5 2 3.4 2.3-1a7 7 0 002.7 1.6L9 21.5h4l.8-2.4a7 7 0 002.7-1.6l2.3 1 2-3.4-2-1.5c.13-.5.2-1.1.2-1.6z" /></svg>
    case 'node':
      return <svg {...common}><rect x="2" y="3" width="20" height="6" rx="1" /><rect x="2" y="15" width="20" height="6" rx="1" /><path d="M6 6h.01M6 18h.01" /></svg>
    case 'public':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
    case 'user':
      return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-7 8-7s8 3 8 7" /></svg>
    default:
      return <svg {...common}><circle cx="12" cy="12" r="9" /></svg>
  }
}
